import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2, Volume2, Mic } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const L = {
  it: {
    title: "Assistente Oasis",
    sub: "Domande sulla villa? Rispondo subito",
    chatgpt: "ChatGPT",
    gemini: "Gemini",
    claude: "Claude",
    placeholder: "Scrivi la tua domanda…",
    hello: "Ciao! Sono l'assistente di Private Oasis Villa. Chiedimi anything sulla villa, della piscina, della posizione o di come prenotare.",
    err: "Connessione non riuscita. Riprova.",
    listen: "Ascolta",
  },
  en: {
    title: "Oasis Assistant",
    sub: "Questions about the villa? I reply instantly",
    chatgpt: "ChatGPT",
    gemini: "Gemini",
    claude: "Claude",
    placeholder: "Type your question…",
    hello: "Hi! I'm the Private Oasis Villa assistant. Ask me anything about the villa, the pool, the location or how to book.",
    err: "Connection failed. Please try again.",
    listen: "Listen",
  },
};

export default function ChatWidget() {
  const [lang, setLang] = useState("it");
  const [open, setOpen] = useState(false);
  const [provider, setProvider] = useState("openai");
  const [msgs, setMsgs] = useState([{ role: "assistant", text: null }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const boxRef = useRef(null);
  const sidRef = useRef(Math.random().toString(36).slice(2));
  const t = L[lang];

  useEffect(() => {
    setLang(document.documentElement.lang === "en" ? "en" : "it");
  }, [open]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [msgs, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setMsgs((m) => [...m, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sidRef.current, provider, text }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          const { delta } = JSON.parse(payload);
          setMsgs((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", text: (copy[copy.length - 1].text || "") + delta };
            return copy;
          });
        }
      }
    } catch (e) {
      setMsgs((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: t.err };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-open"
        onClick={() => setOpen((o) => !o)}
        aria-label="Assistente AI"
        className="fixed bottom-5 left-5 z-40 flex h-13 w-13 items-center justify-center rounded-full bg-ink text-sand shadow-xl shadow-ink/25 transition-transform duration-300 hover:scale-110 md:bottom-8 md:left-8"
      >
        {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-20 left-4 z-40 flex h-[70vh] max-h-[560px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-xl border border-line bg-sand shadow-2xl md:bottom-24 md:left-8"
          >
            <div className="border-b border-line bg-sand2 px-4 py-3">
              <p className="font-serif text-lg leading-tight">{t.title}</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-ink2">{t.sub}</p>
              <div className="mt-2 flex gap-1.5">
                {[["openai", t.chatgpt], ["gemini", t.gemini], ["claude", t.claude]].map(([p, label]) => (
                  <button
                    key={p}
                    data-testid={`chat-provider-${p}`}
                    onClick={() => setProvider(p)}
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                      provider === p ? "bg-olive text-sand" : "border border-line text-ink2 hover:text-ink"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div ref={boxRef} className="flex-1 space-y-3 overflow-y-auto p-4">
              <p className="mx-auto max-w-[85%] rounded-lg bg-sand2 px-3.5 py-2.5 text-sm leading-relaxed text-ink2">
                {t.hello}
              </p>
              {msgs.map((m, i) =>
                m.text === null ? null : (
                  <p
                    key={i}
                    data-testid={`chat-message-${i}`}
                    className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "ml-auto bg-ink text-sand"
                        : "whitespace-pre-wrap border border-line bg-white text-ink"
                    }`}
                  >
                    {m.text}
                    {m.role === "assistant" && m.text && i === msgs.length - 1 && (
                      <button
                        data-testid="chat-tts"
                        onClick={async () => {
                          try {
                            const r = await fetch(`${API}/assistant/tts`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ text: m.text }),
                            });
                            if (!r.ok) throw new Error();
                            const blob = await r.blob();
                            new Audio(URL.createObjectURL(blob)).play();
                          } catch (e) {}
                        }}
                        title={t.listen}
                        className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-line align-middle text-olive transition-colors duration-300 hover:bg-olive hover:text-sand"
                      >
                        <Volume2 className="h-3 w-3" />
                      </button>
                    )}
                    {busy && m.role === "assistant" && i === msgs.length - 1 && !m.text && (
                      <Loader2 className="h-4 w-4 animate-spin text-terra" />
                    )}
                  </p>
                )
              )}
            </div>

            <div className="flex gap-2 border-t border-line p-3">
              <button
                data-testid="chat-mic"
                onClick={async () => {
                  if (recording) {
                    recRef.current?.stop();
                    setRecording(false);
                    return;
                  }
                  try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    const rec = new MediaRecorder(stream);
                    recRef.current = rec;
                    const chunks = [];
                    rec.ondataavailable = (e) => chunks.push(e.data);
                    rec.onstop = async () => {
                      stream.getTracks().forEach((tr) => tr.stop());
                      const blob = new Blob(chunks, { type: "audio/webm" });
                      const fd = new FormData();
                      fd.append("file", blob, "voce.webm");
                      try {
                        const r = await fetch(`${API}/assistant/stt`, { method: "POST", body: fd });
                        if (r.ok) {
                          const { text } = await r.json();
                          if (text) setInput((v) => (v ? v + " " : "") + text);
                        }
                      } catch (e) {}
                    };
                    rec.start();
                    setRecording(true);
                  } catch (e) {}
                }}
                aria-label="Microfono"
                className={`grid h-11 w-11 shrink-0 place-items-center rounded-full border transition-colors duration-300 ${
                  recording ? "border-terra bg-terra text-sand animate-pulse" : "border-line text-ink2 hover:border-olive hover:text-olive"
                }`}
              >
                <Mic className="h-4 w-4" />
              </button>
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t.placeholder}
                className="h-11 flex-1 rounded-full border border-line bg-white px-4 text-sm outline-none placeholder:text-ink2/60 focus:border-olive"
              />
              <button
                data-testid="chat-send"
                onClick={send}
                disabled={busy || !input.trim()}
                aria-label="Invia"
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-terra text-sand transition-colors duration-300 hover:bg-olive disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
